import React from 'react';
import { Plus, X, CalendarRange, Repeat, Info } from 'lucide-react';
import type { SeasonForm, SeasonPriceField, SeasonErrors } from './serviceSeasons';
import { Checkbox } from './ui/Checkbox';
import {
  MAX_DAYS_IN_MONTH, MONTH_NAMES, crossesYearEnd, describeMonthDay, formatMonthDay, isLeapDayBound, pad2, parseMonthDay,
} from '../utils/pricingSeasons';
import { DateField } from './ui/DateTimeField';

/**
 * SAM-589 — seasons editor. New Tailwind component (SAM-175 freeze: no baseui
 * / styletron), mounted from PricingSection. Presentational only: state and
 * the save payload live in useServiceEditor + serviceSeasons.ts.
 *
 * SAM-626 — a season can be "Every year": two month/day pickers write `MM-DD` and the payload
 * gets `every_year: true`; a range whose start is after its end crosses the new year. Off = the
 * dated range below (no `every_year` key). Flipping the switch converts the bounds.
 *
 * Each season is a date range (inclusive) with its own override matrix, laid
 * out like the role price matrix: rows = booking options, columns = the flat
 * price plus one column per active role and per rate group (SAM-588, passed
 * in via `groupColumns`; empty until that ships). A blank cell keeps the base
 * price. Outside every season the base price applies.
 *
 * Pricing-model aware (SAM-591 / SAM-592 integration): `scalarField` picks the
 * replaceable per-option column — `price` (per person, default) or
 * `extra_guest_price` (per unit); `null` hides it (per event size). `bandColumns`
 * adds one column per event size band (`prices_by_event_size`). The caller passes
 * only the columns that apply to the current model.
 */

export interface SeasonOptionRow {
  id: string;
  label: string;
  flatPrice: string;
}

export interface SeasonColumn {
  id: string;
  label: string;
}

interface ServiceSeasonsEditorProps {
  seasons: SeasonForm[];
  options: SeasonOptionRow[];
  roleColumns: SeasonColumn[];
  groupColumns?: SeasonColumn[];
  /** SAM-592 — event size band columns (prices_by_event_size). */
  bandColumns?: SeasonColumn[];
  /** Replaceable per-option column; null = none. Default 'price'. */
  scalarField?: 'price' | 'extra_guest_price' | null;
  /** Header of the scalar column. */
  scalarLabel?: string;
  errors: SeasonErrors;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, field: 'label_en' | 'label_es' | 'start_date' | 'end_date', value: string) => void;
  /** SAM-626 — toggle "Every year" (the caller converts the bounds). */
  onEveryYearChange?: (index: number, on: boolean) => void;
  /** key is the role/group id for the map fields, ignored for `price`. */
  onPriceChange: (index: number, optionId: string, field: SeasonPriceField, key: string, value: string) => void;
}

/** Notes under a recurring row: year-end crossing and the Feb 29 clamp. */
export function recurringHints(season: Pick<SeasonForm, 'start_date' | 'end_date'>): string[] {
  const hints: string[] = [];
  if (crossesYearEnd(season.start_date, season.end_date)) {
    hints.push(`${describeMonthDay(season.start_date)} → ${describeMonthDay(season.end_date)}, crosses the new year`);
  }
  if (isLeapDayBound(season.start_date) || isLeapDayBound(season.end_date)) {
    hints.push('In non-leap years this is Feb 28');
  }
  return hints;
}

/** Month + day selects writing a `MM-DD` string; the day list follows the month (Feb has 29). */
export const MonthDayPicker: React.FC<{
  label: string; name: string; value: string; onChange: (value: string) => void;
  /** Optional class for both selects (the business calendar styles them like its date fields). */
  selectClassName?: string;
}> = ({
  label, name, value, onChange, selectClassName,
}) => {
  const md = parseMonthDay(value);
  const month = md ? md[0] : 0;
  const day = md ? md[1] : 0;
  const days = month ? MAX_DAYS_IN_MONTH[month - 1] : 31;
  // Picking one select fills the other with a default (day 1 / January) so the value is always a
  // complete MM-DD; changing the month clamps the day to what that month has.
  const emit = (m: number, d: number) => {
    onChange(formatMonthDay([m, Math.min(d, MAX_DAYS_IN_MONTH[m - 1])]));
  };
  return (
    <div className="text-xs flex flex-col gap-1" role="group" aria-label={name}>
      <span style={{ color: 'var(--ink-500)' }}>{label}</span>
      <div className="flex gap-1.5">
        <select aria-label={`${name} month`} className={selectClassName} value={month || ''} onChange={(e) => emit(Number(e.target.value), day || 1)}>
          <option value="" disabled>Month</option>
          {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <select aria-label={`${name} day`} className={selectClassName} value={day || ''} onChange={(e) => emit(month || 1, Number(e.target.value))}>
          <option value="" disabled>Day</option>
          {Array.from({ length: days }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{pad2(d)}</option>)}
        </select>
      </div>
    </div>
  );
};

const cellClass = 'w-20 rounded px-1.5 py-1 text-xs';
const cellStyle = { border: '1px solid var(--line)' };

export const ServiceSeasonsEditor: React.FC<ServiceSeasonsEditorProps> = ({
  seasons, options, roleColumns, groupColumns = [], bandColumns = [], scalarField = 'price',
  scalarLabel = 'Season price', errors, onAdd, onRemove, onChange, onEveryYearChange, onPriceChange,
}) => (
  <div className="text-sm">
    <p className="text-xs mb-3" style={{ color: 'var(--ink-500)' }}>
      Optional. Give a date range its own prices (e.g. high season). Dates are inclusive and
      seasons cannot overlap. Switch on &quot;Every year&quot; to repeat a season annually. Leave a cell empty to keep the base price; outside every season
      the base price applies.
    </p>

    {seasons.length === 0 && (
      <p className="text-xs italic mb-3" style={{ color: 'var(--ink-400)' }}>
        No seasons — the base prices apply all year.
      </p>
    )}

    <div className="flex flex-col gap-3 mb-3">
      {seasons.map((season, index) => {
        const seasonErrors = errors.bySeason[index] || [];
        return (
          <div
            key={season.id}
            data-testid={`season-${index}`}
            className="rounded-lg p-3.5"
            style={{ border: `1px solid ${seasonErrors.length ? 'var(--danger, #c0392b)' : 'var(--line)'}`, background: 'var(--surface)' }}
          >
            <div className="flex items-center justify-between mb-2.5">
              <span className="flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: 'var(--ink-700)' }}>
                <CalendarRange size={12} /> Season {index + 1}
              </span>
              <button type="button" className="btn danger" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => onRemove(index)}>
                <X size={11} /> Remove
              </button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              <label className="text-xs flex flex-col gap-1">
                <span style={{ color: 'var(--ink-500)' }}>Label (EN)</span>
                <input value={season.label_en} placeholder="High season" onChange={(e) => onChange(index, 'label_en', e.target.value)} />
              </label>
              <label className="text-xs flex flex-col gap-1">
                <span style={{ color: 'var(--ink-500)' }}>Label (ES)</span>
                <input value={season.label_es} placeholder="Temporada alta" onChange={(e) => onChange(index, 'label_es', e.target.value)} />
              </label>
              {season.every_year ? (
                <>
                  <MonthDayPicker
                    label="Starts" name={`Season ${index + 1} start`} value={season.start_date}
                    onChange={(v) => onChange(index, 'start_date', v)}
                  />
                  <MonthDayPicker
                    label="Ends (inclusive)" name={`Season ${index + 1} end`} value={season.end_date}
                    onChange={(v) => onChange(index, 'end_date', v)}
                  />
                </>
              ) : (
                <>
                  <DateField
                    label="Start date" ariaLabel={`Season ${index + 1} start date`} value={season.start_date}
                    onChange={(v) => onChange(index, 'start_date', v)}
                  />
                  <DateField
                    label="End date (inclusive)" ariaLabel={`Season ${index + 1} end date`} value={season.end_date}
                    onChange={(v) => onChange(index, 'end_date', v)}
                  />
                </>
              )}
            </div>

            {onEveryYearChange && (
              <div className="mb-3 flex flex-col gap-1.5">
                <Checkbox
                  checked={season.every_year === true}
                  onChange={(e) => onEveryYearChange(index, e.target.checked)}
                  aria-label={`Season ${index + 1} every year`}
                  style={{ fontSize: 12, color: 'var(--ink-700)' }}
                >
                  <Repeat size={12} /> <span className="font-medium">Every year</span>
                  <span style={{ color: 'var(--ink-400)' }}>&nbsp;— repeats on these days each year</span>
                </Checkbox>
                {season.every_year && recurringHints(season).map((hint) => (
                  <p key={hint} data-testid="season-hint" className="text-xs flex items-center gap-1.5" style={{ color: 'var(--ink-500)' }}>
                    <Info size={12} /> {hint}
                  </p>
                ))}
              </div>
            )}

            {seasonErrors.map((msg) => (
              <p key={msg} role="alert" className="text-xs mb-2" style={{ color: 'var(--danger, #c0392b)' }}>{msg}</p>
            ))}

            {options.length === 0 ? (
              <p className="text-xs italic" style={{ color: 'var(--ink-400)' }}>
                Add booking options above to set seasonal prices.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--line)' }}>
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr style={{ background: 'var(--surface-2)' }}>
                      <th className="text-left font-medium px-2 py-2" style={{ color: 'var(--ink-500)' }}>Guest type</th>
                      {scalarField && (
                        <th className="text-left font-medium px-2 py-2" style={{ color: 'var(--ink-700)' }}>{scalarLabel}</th>
                      )}
                      {bandColumns.map((c) => (
                        <th key={`b-${c.id}`} className="text-left font-medium px-2 py-2" style={{ color: 'var(--ink-700)' }}>{c.label}</th>
                      ))}
                      {roleColumns.map((c) => (
                        <th key={`r-${c.id}`} className="text-left font-medium px-2 py-2" style={{ color: 'var(--ink-700)' }}>{c.label}</th>
                      ))}
                      {groupColumns.map((c) => (
                        <th key={`g-${c.id}`} className="text-left font-medium px-2 py-2" style={{ color: 'var(--ink-700)' }}>{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {options.map((option) => {
                      const entry = season.prices[option.id];
                      return (
                        <tr key={option.id} style={{ borderTop: '1px solid var(--line-subtle)' }}>
                          <td className="px-2 py-2 align-top">
                            <div style={{ color: 'var(--ink-900)' }}>{option.label || option.id}</div>
                            <div className="text-[11px]" style={{ color: 'var(--ink-400)' }}>base: {option.flatPrice || '0'}</div>
                          </td>
                          {scalarField && (
                            <td className="px-2 py-2 align-top">
                              <input
                                type="number" step="0.01" min={0} className={cellClass} style={cellStyle}
                                aria-label={`Season ${index + 1} ${option.label || option.id} ${scalarField === 'price' ? 'price' : 'extra guest price'}`}
                                value={entry?.[scalarField] ?? ''} placeholder="base"
                                onChange={(e) => onPriceChange(index, option.id, scalarField, '', e.target.value)}
                              />
                            </td>
                          )}
                          {bandColumns.map((c) => (
                            <td key={`b-${c.id}`} className="px-2 py-2 align-top">
                              <input
                                type="number" step="0.01" min={0} className={cellClass} style={cellStyle}
                                aria-label={`Season ${index + 1} ${option.label || option.id} ${c.label} price`}
                                value={entry?.prices_by_event_size?.[c.id] ?? ''} placeholder="base"
                                onChange={(e) => onPriceChange(index, option.id, 'prices_by_event_size', c.id, e.target.value)}
                              />
                            </td>
                          ))}
                          {roleColumns.map((c) => (
                            <td key={`r-${c.id}`} className="px-2 py-2 align-top">
                              <input
                                type="number" step="0.01" min={0} className={cellClass} style={cellStyle}
                                aria-label={`Season ${index + 1} ${option.label || option.id} ${c.label} price`}
                                value={entry?.prices_by_role[c.id] ?? ''} placeholder="base"
                                onChange={(e) => onPriceChange(index, option.id, 'prices_by_role', c.id, e.target.value)}
                              />
                            </td>
                          ))}
                          {groupColumns.map((c) => (
                            <td key={`g-${c.id}`} className="px-2 py-2 align-top">
                              <input
                                type="number" step="0.01" min={0} className={cellClass} style={cellStyle}
                                aria-label={`Season ${index + 1} ${option.label || option.id} ${c.label} price`}
                                value={entry?.prices_by_group[c.id] ?? ''} placeholder="base"
                                onChange={(e) => onPriceChange(index, option.id, 'prices_by_group', c.id, e.target.value)}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>

    <button type="button" className="btn ghost w-full justify-center" onClick={onAdd}>
      <Plus size={14} /> Add season
    </button>
  </div>
);
